import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getPresignedUrl,
  putImgWithPresignedUrl,
  updateClassified,
} from "../../../services/main-service/mainService";
import { ImageFile } from "../../../types/main-types/mainTypes";
import CreateAdInput from "../../../components/main-layout-components/create-ad-input/CreateAdInput";
import ImagePreview from "../../../components/main-layout-components/image-preview/ImagePreview";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { FiUpload } from "react-icons/fi";
import { toastError, toastSuccess } from "../../../utils/toast";
import {
  addThousandSeparotors,
  isFormValid,
  removeThousandSeparotors,
} from "../../../utils/formValidations";
import {
  FUEL_OPTIONS,
  TRANSMISSION_OPTIONS,
} from "../../../configs/dropdown-values/dropdownValues";
import Spinner from "../../../components/common/spinner/Spinner";
import { useMasterData } from "../../../hooks/useMasterData";
import "../../../styles/css/data-table.css";

const requiredFields = ["year", "category", "price", "engine_cc", "description", "image_urls"];

const initialFormValues = {
  category: "",
  description: "",
  engineCc: "",
  fuel: "",
  location: "",
  make: "",
  mileage: "",
  model: "",
  negotiable: false,
  numberOfOwners: "",
  price: "",
  transmission: "",
  year: "",
};

function UpdateAdPage() {
  const { masterData, masterDataLoading } = useMasterData();
  const navigate = useNavigate();
  const { state } = useLocation();

  let [id] = useState<string>(state.id);
  let [uploadedFileList, setUploadedFileList] = useState<ImageFile[]>([]);
  let [isLoading, setIsLoading] = useState<boolean>(false);

  let [formData, setFormData] = useState<any>({
    category:       state.category,
    description:    state.description,
    engineCc:       addThousandSeparotors(state.engine_cc),
    fuel:           state.fuel,
    location:       state.location,
    make:           state.make,
    mileage:        addThousandSeparotors(state.mileage),
    model:          state.model,
    negotiable:     state.negotiable,
    numberOfOwners: addThousandSeparotors(state.number_of_owners),
    price:          addThousandSeparotors(state.price),
    transmission:   state.transmission,
    year:           state.year,
  });

  function handleInputChange(
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
      | React.ChangeEvent<HTMLTextAreaElement>,
    stateName: string,
    inputType: string
  ) {
    let outputValue: string;
    if (inputType === "number" && stateName !== "year") {
      outputValue = addThousandSeparotors(e.target.value);
    } else if (stateName === "year") {
      outputValue = e.target.value.replace(/\D/g, "");
    } else {
      outputValue = e.target.value;
    }
    setFormData((prev: any) => ({ ...prev, [stateName]: outputValue }));
  }

  function handleClose(f: ImageFile) {
    setUploadedFileList((prev) => prev.filter((fi) => fi !== f));
  }

  async function handleFileUploads(e: React.ChangeEvent<HTMLInputElement>) {
    let imgFilesList: FileList | null = e.target.files;
    if (!imgFilesList) return;

    if (imgFilesList.length > 5) {
      toastError("Maximum 5 images allowed");
      e.target.files = null;
      return;
    }

    const newFiles: ImageFile[] = Array.from(imgFilesList).map((file) => ({
      Obj: file,
      Url: URL.createObjectURL(file),
      IsNew: true,
    }));
    setUploadedFileList((prev) => [...prev, ...newFiles]);
  }

  async function getImgUrls(fileList: ImageFile[]) {
    let imgUrlList: (string | void)[] = [];
    for (const file of fileList) {
      if (!file?.IsNew) {
        imgUrlList.push(file.Key);
        continue;
      }
      let payload = {
        content_type: file.Obj.type,
        extension: file.Obj.name.split(".").pop(),
        number_of_files: 1,
      };
      try {
        let presignedUrlResponse = await getPresignedUrl(payload);
        let preSignedUrl: string = presignedUrlResponse[0].url;
        let preSignedKey: string = presignedUrlResponse[0].key;
        let configs = { headers: { "Content-Type": file.Obj.type, "x-amz-acl": "public-read" } };
        await putImgWithPresignedUrl(preSignedUrl, file.Obj, configs);
        imgUrlList.push(preSignedKey);
      } catch (error: any) {
        console.log("image upload error:", error.response?.data?.message);
      }
    }
    return imgUrlList;
  }

  async function handlePublish() {
    setIsLoading(true);
    let imgUrlList = await getImgUrls(uploadedFileList);
    let payload = {
      id,
      category:         formData.category,
      description:      formData.description,
      engine_cc:        removeThousandSeparotors(formData.engineCc),
      fuel:             formData.fuel,
      image_urls:       imgUrlList,
      location:         formData.location,
      make:             formData.make,
      mileage:          removeThousandSeparotors(formData.mileage),
      model:            formData.model,
      negotiable:       formData.negotiable.toString().toLowerCase() === "true",
      number_of_owners: removeThousandSeparotors(formData.numberOfOwners),
      price:            removeThousandSeparotors(formData.price),
      transmission:     formData.transmission,
      year:             removeThousandSeparotors(formData.year),
    };

    if (!isFormValid(requiredFields, payload).isValid) {
      toastError("Please fill all required fields");
      setIsLoading(false);
      return;
    }

    try {
      await updateClassified(payload);
      setFormData(initialFormValues);
      setUploadedFileList([]);
      window.scrollTo(0, 0);
      toastSuccess("Changes submitted — they'll be published after approval.");
      navigate(-1);
    } catch (error: any) {
      toastError(error.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const f = async () => {
      const p = state.images.map(async (x: any) => {
        const url  = x.url;
        const key  = x.key;
        const response = await fetch(url);
        const data = await response.blob();
        const ii   = url.split("/");
        const file = new File([data], ii[ii.length - 1], { type: data.type });
        return { Obj: file, Url: url, IsNew: false, Key: key };
      });
      const out = await Promise.all(p);
      setUploadedFileList((prev) => [...prev, ...out]);
    };
    f();
  }, []);

  if (masterDataLoading) return <Spinner />;

  const categoryOptions =
    masterData?.VehicleCategories?.length > 0
      ? masterData.VehicleCategories.map((c: any) => ({ key: c.name, value: c.name }))
      : [];

  const locationOptions =
    masterData?.Locations?.length > 0
      ? masterData.Locations.map((l: any) => ({ key: l, value: l }))
      : [];

  return (
    <div className="form-page">
      <div className="container">
        <div className="form-page__card">
          {/* Header */}
          <div className="form-page__header">
            <button className="data-page__back-btn" onClick={() => navigate(-1)}>
              <MdOutlineArrowBackIosNew size={18} />
            </button>
            <div>
              <h4 className="form-page__title">Update Ad</h4>
              <p className="form-page__subtitle">
                Changes will be reviewed before publishing
              </p>
            </div>
          </div>

          {/* Form body */}
          <div className="form-page__body">
            {/* Vehicle Details */}
            <div className="form-section">
              <p className="form-section__label">Vehicle Details</p>
              <div className="row g-3">
                {[
                  { inputLabel: "Make",  inputType: "text",   colStateName: "make",  isRequired: false },
                  { inputLabel: "Model", inputType: "text",   colStateName: "model", isRequired: false },
                  { inputLabel: "Year",  inputType: "number", colStateName: "year",  isRequired: true  },
                ].map((col) => (
                  <div key={col.colStateName} className="col-sm-12 col-md-4">
                    <CreateAdInput
                      inputLabel={col.inputLabel}
                      inputType={col.inputType}
                      optionList={[]}
                      value={formData[col.colStateName]}
                      handleInputChange={handleInputChange}
                      stateName={col.colStateName}
                      isRequired={col.isRequired}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Listing Details */}
            <div className="form-section">
              <p className="form-section__label">Listing Details</p>
              <div className="row g-3">
                <div className="col-sm-12 col-md-4">
                  <CreateAdInput
                    inputLabel="Category" inputType="select"
                    optionList={categoryOptions}
                    value={formData.category}
                    handleInputChange={handleInputChange}
                    stateName="category" isRequired={true}
                  />
                </div>
                <div className="col-sm-12 col-md-4">
                  <CreateAdInput
                    inputLabel="Location" inputType="select"
                    optionList={locationOptions}
                    value={formData.location}
                    handleInputChange={handleInputChange}
                    stateName="location" isRequired={false}
                  />
                </div>
                <div className="col-sm-12 col-md-4">
                  <CreateAdInput
                    inputLabel="Price" inputType="number"
                    optionList={[]}
                    value={formData.price}
                    handleInputChange={handleInputChange}
                    stateName="price" isRequired={true}
                  />
                </div>
              </div>
            </div>

            {/* Specifications */}
            <div className="form-section">
              <p className="form-section__label">Specifications</p>
              <div className="row g-3">
                {[
                  { inputLabel: "Mileage",        inputType: "number", colStateName: "mileage",        optionList: [],                  isRequired: false },
                  { inputLabel: "Transmission",   inputType: "select", colStateName: "transmission",   optionList: TRANSMISSION_OPTIONS, isRequired: false },
                  { inputLabel: "Fuel",           inputType: "select", colStateName: "fuel",           optionList: FUEL_OPTIONS,         isRequired: false },
                  { inputLabel: "Engine",         inputType: "number", colStateName: "engineCc",       optionList: [],                  isRequired: true  },
                  { inputLabel: "No of owners",   inputType: "number", colStateName: "numberOfOwners", optionList: [],                  isRequired: false },
                ].map((col) => (
                  <div key={col.colStateName} className="col-sm-12 col-md-4">
                    <CreateAdInput
                      inputLabel={col.inputLabel}
                      inputType={col.inputType}
                      optionList={col.optionList}
                      value={formData[col.colStateName]}
                      handleInputChange={handleInputChange}
                      stateName={col.colStateName}
                      isRequired={col.isRequired}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="form-section">
              <p className="form-section__label">Description</p>
              <div className="form-floating">
                <textarea
                  className="form-control"
                  placeholder="Describe the vehicle"
                  id="floatingTextarea"
                  style={{ minHeight: 120 }}
                  value={formData.description}
                  onChange={(e) => handleInputChange(e, "description", "text")}
                />
                <label htmlFor="floatingTextarea">
                  Description <span className="text-danger">*</span>
                </label>
              </div>
            </div>

            {/* Images */}
            <div className="form-section">
              <p className="form-section__label">Images <span style={{ color: "#ed1b24" }}>*</span></p>
              <label htmlFor="formImgMultipleInput" className="form-page__file-btn mb-3">
                <FiUpload size={16} />
                Choose images (max 5)
              </label>
              <input
                type="file"
                id="formImgMultipleInput"
                multiple
                onChange={handleFileUploads}
                style={{ display: "none" }}
              />
              {uploadedFileList.length > 0 && (
                <ImagePreview imageList={uploadedFileList} handleClose={handleClose} />
              )}
            </div>

            {/* Options + Submit */}
            <div className="form-page__submit">
              <div className="form-check d-inline-flex align-items-center gap-2 mb-4">
                <input
                  className="form-check-input mt-0"
                  type="checkbox"
                  id="checkNegotiable"
                  checked={formData.negotiable === true || formData.negotiable === "true"}
                  onChange={(e) =>
                    setFormData((prev: any) => ({ ...prev, negotiable: e.target.checked }))
                  }
                  style={{ cursor: "pointer" }}
                />
                <label className="form-check-label" htmlFor="checkNegotiable" style={{ cursor: "pointer", fontSize: "0.9375rem", color: "#374151" }}>
                  Price is negotiable
                </label>
              </div>
              <div>
                <button
                  className="btn btn-danger px-5 py-2 fw-bold"
                  onClick={handlePublish}
                  disabled={isLoading}
                  style={{ minWidth: 160 }}
                >
                  {isLoading ? "Updating…" : "Update Ad"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpdateAdPage;
